import { TranslateService } from '@ngx-translate/core';
import {Component, Inject, OnInit} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {HttpLoaderService, KeyValuePair, ValidationMessage} from '@tymes4-shared';
import {ConfirmService, T4Validators} from '@tymes4-shared';
import {PrintPdfComponent} from '../../print-pdf/print-pdf.component';
import { CurrencyPipe } from '@angular/common';
import moment from 'moment';
import { CustomValidators } from '@narik/custom-validators';
import {
  CashRegisterBooking,
  CashRegisterPaymentTypeEnum,
  CashRegisterService,
  DirectDebitPaymentInitiationArgs,
  DirectDebitPaymentMethod,
  GetDirectDebitFormArgs,
  OrderLineView,
  OrderPaymentInfo,
  ProductTypeEnum
} from "../../../api";
import { ApplicationModuleHelperService, ApplicationModules } from '../../../services/application-module.service';
import { forkJoin } from 'rxjs';

interface OrderPaymentBookingInfo extends OrderPaymentInfo {
  incassoBookingState?: 'successful' | 'running' | 'failed';
}

@Component({
    selector: 'app-payment',
    templateUrl: './payment.component.html',
    styleUrls: ['./payment.component.scss'],
    standalone: false
})
export class PaymentComponent implements OnInit {
  public orderPaymentQRCodeActive: boolean;

  public form: FormGroup;

  // these are subgroups of form
  public formCash: FormGroup;
  public formIncasso: FormGroup;
  public formCredit: FormGroup;
  public formOther: FormGroup;

  public amountOpen = 0;
  public paymentSurcharge = 0;
  public amountPaidPin: number;
  public amountPaidCash: number;
  public amountPaidOther: number;
  public orderIds: number[];

  public selectedTabId: string;
  public requiredOrderPayments: OrderPaymentBookingInfo[] = null;
  public canUseDirectDebit: boolean = null;
  public directDebitInstalments: DirectDebitPaymentMethod[];
  public allOrdersHaveEmailsSpecified: boolean = null;
  public instalments = 0;

  public validationMessagesCash: KeyValuePair<ValidationMessage[]> = {};
  public validationMessagesIncasso: KeyValuePair<ValidationMessage[]> = {};
  public validationMessagesCredit: KeyValuePair<ValidationMessage[]> = {
    AmountPaidCredit: [{ type: 'maxCredit', message: 'SALES.CASH-REGISTER.DIALOG.PAYMENT.VALIDATION.MAXCREDIT' }]
  };
  public validationMessagesOther: KeyValuePair<ValidationMessage[]> = {};

  public qrCodeValue: string;
  public qrCodeLabel: string;

  payOrReturnOptions = [
    { label: this.translate.instant('DIALOGS.CASH-REGISTER.PAYMENT-DIRECTION.PAY'), value: false },
    { label: this.translate.instant('DIALOGS.CASH-REGISTER.PAYMENT-DIRECTION.RETURN'), value: true }
  ];

  incassoBeingProcessed: boolean = null;
  incassoProcessFullySuccessful: boolean = null;
  bookingCustomerId: number;
  directDebitFormReceived = false;
  constructor(@Inject(MAT_DIALOG_DATA) public passedData: any,
              public dialogRef: MatDialogRef<PaymentComponent>,
              public cashRegisterService: CashRegisterService,
              private applicationModuleHelperService: ApplicationModuleHelperService,
              public apploader: HttpLoaderService,
              public confirmService: ConfirmService,
              public currencyPipe: CurrencyPipe,
              private dialog: MatDialog,
              private translate: TranslateService) { }

  ngOnInit() {
    this.bookingCustomerId = this.passedData.bookingCustomerId;
    this.orderIds = this.passedData.payload;

    this.apploader.open();

    this.formCash = new FormGroup({
      AmountPaidPin: new FormControl('', [Validators.min(0)]),
      AmountPaidCash: new FormControl('', [Validators.min(0)])
    });

    this.formIncasso = new FormGroup({
      Iban: new FormControl('', [T4Validators.validateIban]),
      AccountName: new FormControl('', [Validators.required]),
      PaymentMethod: new FormControl('', [Validators.required]),
    });

    for (let i = 1; i <= 12; i++) {
      const key = `DateEffective${i}`;
      this.formIncasso.addControl(key, new FormControl('', [Validators.required]));
      this.validationMessagesIncasso[key] = [
        { type: 'maxDate', message: '' },
        { type: 'minDate', message: '' },
      ];
    }

    this.formCredit = new FormGroup({
      AmountPaidCredit: new FormControl('', [Validators.required, Validators.min(0), this.validateMaxCredit.bind(this)]),
      IsReturning: new FormControl({ value: false, disabled: this.orderIds.length != 1 })
    });

    this.formOther = new FormGroup({
      AmountPaidOther: new FormControl('', [Validators.min(0)])
    });

    this.form = new FormGroup({
      Cash: this.formCash,
      Incasso: this.formIncasso,
      Credit: this.formCredit,
      Other: this.formOther,

      Description: new FormControl(''),
      ConfirmReturnRegister: new FormControl(false),
      ConfirmCheckComplete: new FormControl(false)
    });

    forkJoin([
      this.cashRegisterService.getOrderPaymentInfo({ OrderIds: this.orderIds }),
      this.applicationModuleHelperService.getActiveModules()
    ]).subscribe({
      next: ([paymentInfo, activeModules]) => {

        this.amountOpen = 0;
        this.requiredOrderPayments = paymentInfo;

        for (const item of paymentInfo) {
          this.amountOpen += item.Remaining;
        }

        this.orderPaymentQRCodeActive = activeModules.includes(ApplicationModules.OrderPaymentQRCode);

        let tabId = 'cash';
        if (this.orderPaymentQRCodeActive) {
          if (this.tryGenerateQRCodeValue()) {
            tabId = 'other';
          }
        }
        this.changeTab(tabId);

        this.apploader.close();
      },
      error: (error) => {
        this.apploader.close();
        this.handleGenericError();
      }
    });
  }

  getCustomerTotalCredit() {
    if (!this.requiredOrderPayments?.length) {
      return null;
    }

    const customerId = this.requiredOrderPayments[0].CustomerId;
    const customerTotalCredit = this.requiredOrderPayments[0].CustomerTotalCredit ?? 0;

    // Only return something if all orders are for the same customer
    for (let i = 1; i < this.requiredOrderPayments.length; i++) {
      if (this.requiredOrderPayments[i].CustomerId != customerId || (this.requiredOrderPayments[i].CustomerTotalCredit ?? 0) != customerTotalCredit) {
        return null;
      }
    }

    return customerTotalCredit;
  }

  closePayment(status) {
    this.dialogRef.close(status);
  }

  doBooking() {
    if (!this.form.valid) {
      return;
    }

    const amountPaidPin = this.formCash.controls['AmountPaidPin'].value;
    const amountPaidCash = this.formCash.controls['AmountPaidCash'].value;

    const description = this.form.controls['Description'].value;

    const bookings: CashRegisterBooking[] = [];
    if (amountPaidPin) {
      bookings.push({
        CashRegisterPaymentType: CashRegisterPaymentTypeEnum.Pin,
        Amount: amountPaidPin
      });
    }
    if (amountPaidCash) {
      bookings.push({
        CashRegisterPaymentType: CashRegisterPaymentTypeEnum.Cash,
        Amount: amountPaidCash
      });
    }

    this.apploader.open();
    this.cashRegisterService.bookCashRegisterPayments({ OrderIds: this.orderIds, Bookings: bookings, Description: description }).subscribe((moneyToReturn : number) => {

      this.apploader.close();

      const clientMoneyToReturn = (amountPaidPin || 0) + (amountPaidCash || 0) - this.amountOpen;

      if (!(moneyToReturn > clientMoneyToReturn && clientMoneyToReturn > 0)) {
        this.dialogRef.close(true);
        return;
      }

      this.confirmService.confirm({
        title: 'SALES.CASH-REGISTER.DIALOG.PAYMENT.CONFIRM.MONEY-TO-RETURN.TITLE',
        message: 'SALES.CASH-REGISTER.DIALOG.PAYMENT.CONFIRM.MONEY-TO-RETURN.MSG',
        messageParams: {
          moneyToReturn: this.currencyPipe.transform(moneyToReturn, 'EUR', 'symbol', '1.2-2'),
          clientMoneyToReturn: this.currencyPipe.transform(clientMoneyToReturn, 'EUR', 'symbol', '1.2-2')
        },
        okonly: true
      }).subscribe(result => {
        this.dialogRef.close(true);
      });
    }, (error) => {
      this.apploader.close();
      this.handleGenericError();
    });
  }

  doBookingIncasso() {
    if (!this.form.valid) {
      return;
    }

    this.incassoBeingProcessed = true;
    const iban = this.formIncasso.controls['Iban'].value;
    const accountName = this.formIncasso.controls['AccountName'].value;
    const paymentMethod = this.formIncasso.controls['PaymentMethod'].value;

    let isFirst = true;

    for (const ep of this.requiredOrderPayments) {
      if (ep.Remaining <= 0) {
        ep.incassoBookingState = 'successful';
        continue;
      }

      ep.incassoBookingState = 'running';
      const effectiveDates = this.extractEffectiveDates();
      const directDebitPaymentInitiationArgs: DirectDebitPaymentInitiationArgs = {
        OrderId: ep.OrderId,
        DirectDebitInitiation: {
          IBAN: iban,
          AccountName: accountName,
          PaymentMethod: paymentMethod,
          EffectiveDates: effectiveDates
        }
      }

      this.cashRegisterService.initiateCashRegisterDirectDebit(directDebitPaymentInitiationArgs).subscribe((result) => {
        if (result.OK) {
          ep.incassoBookingState = 'successful';
          this.refreshIncassoBeingProcessedStatus();
          return;
        }

        if (isFirst) {
          isFirst = false;

          this.confirmService.confirm({
            title: 'SALES.CASH-REGISTER.DIALOG.PAYMENT.CONFIRM.DIRECT-DEBIT-FAILED.TITLE',
            message: 'SALES.CASH-REGISTER.DIALOG.PAYMENT.CONFIRM.DIRECT-DEBIT-FAILED.MSG',
            okonly: true
          }).subscribe();
        }

        ep.incassoBookingState = 'failed';

        this.refreshIncassoBeingProcessedStatus();
      });
    }

    this.refreshIncassoBeingProcessedStatus();
  }

  refreshIncassoBeingProcessedStatus() {
    if (this.requiredOrderPayments.filter(ep => ep.incassoBookingState === 'running').length === 0) {
      this.incassoProcessFullySuccessful = this.requiredOrderPayments.some(ep => ep.incassoBookingState === 'failed') || this.requiredOrderPayments.find(x => x.incassoBookingState !== undefined) == undefined;
      this.incassoBeingProcessed = false;
    }
  }

  changeTab(newSelectedTabId: string) {

    this.selectedTabId = newSelectedTabId;

    this.unCheckConfirmOnChange();

    setIsEnabled(this.formCash, newSelectedTabId == 'cash');
    setIsEnabled(this.formIncasso, newSelectedTabId == 'directdebit');
    setIsEnabled(this.formCredit, newSelectedTabId == 'credit');
    setIsEnabled(this.formOther, newSelectedTabId == 'other');

    if (newSelectedTabId === 'directdebit') {

      this.apploader.open();
      const anyOrderWithoutCollectPaymentAllowed = this.requiredOrderPayments.filter(op => op.CollectPaymentAllowed === false)[0];
      const anyOrderWithoutEmail = this.requiredOrderPayments.filter(op => op.CustomerHasEmailSpecified === false)[0];

      if (this.requiredOrderPayments.filter(op => op.CollectPaymentAllowed === true).length > 0) {
        this.directDebitInstalments = this.requiredOrderPayments.filter(op => op.CollectPaymentAllowed === true)[0].DirectDebitPaymentMethods;
        if (!this.formIncasso.controls.PaymentMethod.value && this.directDebitInstalments.length > 0) {
          this.formIncasso.controls.PaymentMethod.setValue(this.directDebitInstalments[0].Id);
          this.setDirectDebitPaymentMethod(this.directDebitInstalments[0]);
          this.paymentSurcharge = this.directDebitInstalments[0].Surcharge || 0;
        }
      }
      this.canUseDirectDebit = !anyOrderWithoutCollectPaymentAllowed;
      this.allOrdersHaveEmailsSpecified = !anyOrderWithoutEmail;

      this.apploader.close();
    }
  }

  onPayOrReturnChange(e) {
    if (e.value) {
      this.form.controls.ConfirmReturnRegister.enable();
      this.form.controls.ConfirmReturnRegister.addValidators(Validators.requiredTrue);
      this.form.controls.ConfirmCheckComplete.addValidators(Validators.requiredTrue);
    }
    else {
      this.form.controls.ConfirmReturnRegister.disable();
      this.form.controls.ConfirmReturnRegister.removeValidators(Validators.requiredTrue);
      this.form.controls.ConfirmCheckComplete.removeValidators(Validators.requiredTrue);
    }

    this.form.controls.ConfirmReturnRegister.updateValueAndValidity();
    this.form.controls.ConfirmCheckComplete.updateValueAndValidity();
    this.formCredit.controls.AmountPaidCredit.updateValueAndValidity();
    this.refreshIncassoBeingProcessedStatus();
  }

  changePaymentMethod(e) {
    this.setDirectDebitPaymentMethod(e);

    // correct amount to be charged
    this.paymentSurcharge = e.Surcharge || 0;
  }

  setDirectDebitPaymentMethod(paymentMethod: any) {
    this.instalments = paymentMethod.InstalmentCount;

    let minDate = moment(new Date());

    for (let i = 1; i <= 12; i++) {
      const key = `DateEffective${i}`;
      if (paymentMethod.Instalments.length < i) {
        this.formIncasso.controls[key].removeValidators([Validators.required]);
        this.formIncasso.controls[key].setValue(null);
        continue;
      }

      const instalment = paymentMethod.Instalments[i - 1];
      const maxMoment = moment(instalment.Date);
      this.changeMinMaxDateErrorMessage(key, minDate, maxMoment);
      this.formIncasso.controls[key].setValue(instalment.Date);

      const validators = [
        Validators.required,
        CustomValidators.minDate(minDate),
        CustomValidators.maxDate(maxMoment.add(1, 'days'))
      ];
      this.formIncasso.controls[key].setValidators(validators);

      if (!instalment.IsFlexible) {
        this.formIncasso.controls[key].disable();
      } else {
        this.formIncasso.controls[key].enable();
      }

      minDate = maxMoment;
    }
  }

  validateMaxCredit(control: AbstractControl): ValidationErrors {
    if (!this.formCredit || this.formCredit.controls.IsReturning.value) {
      return null;
    }

    const customerTotalCredit = this.getCustomerTotalCredit();
    if (customerTotalCredit == null) {
      return null;
    }

    const value = control.value;
    if (value > customerTotalCredit) {
      return { maxCredit: true };
    }
    return null;
  }

  changeMinMaxDateErrorMessage(formField: string, minDate: moment.Moment, maxDate: moment.Moment) {
    const fieldMessages = this.validationMessagesIncasso[formField];
    if (!fieldMessages) {
      return;
    }

    for (const detail in fieldMessages) {
      if (fieldMessages[detail].type === 'maxDate') {
        fieldMessages[detail].message = this.translate.instant('SALES.CASH-REGISTER.DIALOG.PAYMENT.VALIDATION.MAXDATE', { date: maxDate.format('DD-MM-YYYY')});
      }
      if (fieldMessages[detail].type === 'minDate') {
        fieldMessages[detail].message = this.translate.instant('SALES.CASH-REGISTER.DIALOG.PAYMENT.VALIDATION.MINDATE', {date: minDate.format('DD-MM-YYYY')});
      }
    }
  }

  downloadDirectDebitForm() {
    this.apploader.open();
    const iban = this.formIncasso.controls['Iban'].value;
    const accountName = this.formIncasso.controls['AccountName'].value;
    const paymentMethod = this.formIncasso.controls['PaymentMethod'].value;

    const effectiveDates = this.extractEffectiveDates();
    const getDirectDebitFormArgs: GetDirectDebitFormArgs = {
      BookingCustomerId: this.bookingCustomerId,
      OrderIds: this.orderIds,
      DirectDebitInitiation: {
        IBAN: iban,
        AccountName: accountName,
        PaymentMethod: paymentMethod,
        EffectiveDates: effectiveDates
      }
    }

    this.cashRegisterService.getDirectDebitForm(getDirectDebitFormArgs).subscribe((pdf) => {
      this.directDebitFormReceived = true;
      this.apploader.close();

      const pdfDialogdialogRef: MatDialogRef<any> = this.dialog.open(PrintPdfComponent,
        {
          panelClass: 'full-width-dialog',
          disableClose: false,
          height: '90%',
          width: '90%',
          maxWidth: '98vw',
          data: {
            blob: pdf
          }
        });

      pdfDialogdialogRef.afterClosed().subscribe();
    });
  }

  doBookingCredit() {
    if (!this.form.valid) {
      return;
    }

    const description = this.form.controls.Description.value;
    const amount = this.formCredit.controls.AmountPaidCredit.value;
    const isReturning = this.formCredit.controls.IsReturning.value;

    if (isReturning && this.orderIds?.length != 1) {
      return;
    }

    this.apploader.open();

    let request = isReturning
      ? this.cashRegisterService.returnCashRegisterCredit({ OrderId: this.orderIds[0], ReturnAmount: amount, Description: description })
      : this.cashRegisterService.bookCashRegisterCredit({ OrderIds: this.orderIds, BookingAmount: amount, Description: description });

    request.subscribe({
      next: () => {
        this.apploader.close();
        this.dialogRef.close(true);
      },
      error: () => {
        this.apploader.close();
        this.handleGenericError();
      }
    });
  }

  doBookingOther() {
    if (!this.form.valid) {
      return;
    }

    const description = this.form.controls['Description'].value;
    const amountPaidOther = this.formOther.controls['AmountPaidOther'].value;
    const confirmReturnRegister = this.form.controls['ConfirmReturnRegister'].value;
    const booking = [];

    if (amountPaidOther) {
      booking.push({
        CashRegisterPaymentType: 3,
        Amount: amountPaidOther
      });
    }

    this.apploader.open();
    this.cashRegisterService.bookCashRegisterOtherPayments({ OrderIds: this.orderIds, Description: description, Booking: booking, ConfirmReturnRegister: confirmReturnRegister}).subscribe((moneyToReturn) => {
      this.apploader.close();
      this.dialogRef.close(true);
    }, (error) => {
        this.apploader.close();
        this.handleGenericError();
      });
  }

  private handleGenericError() {
    this.confirmService.confirm({
      title: 'SALES.CASH-REGISTER.DIALOG.PAYMENT.CONFIRM.ERROR.TITLE',
      message: 'SALES.CASH-REGISTER.DIALOG.PAYMENT.CONFIRM.PROCESS-ERROR.MSG',
      okonly: true
    }).subscribe((result: boolean) => {
      this.dialogRef.close(false);
    });
  }

  tryGenerateQRCodeValue(): boolean {
    if (this.requiredOrderPayments?.length != 1) {
      this.qrCodeLabel = 'SALES.CASH-REGISTER.DIALOG.PAYMENT.QR-CODE.ERRORS.NOT-SINGLE-ORDER';
      return false;
    }

    const order = this.requiredOrderPayments[0];

    // Format is defined in TPD-4285
    const toEncode = {
      OrderId: order.OrderId,
      Created: moment.utc(order.OrderDate).toISOString(),
      CustomerNumber: order.CustomerNumber,
      TotalExVat: order.TotalExVat,
      TotalInclVat: order.TotalInclVat,
      Vat: order.VAT,
      AmountPayed: order.TotalInclVat - order.OrderBalance,
      Balance: order.OrderBalance,
      DeliveryMethodName: order.DeliveryMethodName,
      OrderLines: this.generateOrderLinesSummary(order.OrderLines)
    };
    let qrCodeValue = JSON.stringify(toEncode);

    // In theory we could generate QR codes larger than this (up to 2953 characters), but in practice it becomes too large to display.
    if (qrCodeValue.length > 1465) {
      this.qrCodeLabel = 'SALES.CASH-REGISTER.DIALOG.PAYMENT.QR-CODE.ERRORS.TOO-LARGE';
      return false;
    }

    this.qrCodeValue = qrCodeValue;
    this.qrCodeLabel = 'SALES.CASH-REGISTER.DIALOG.PAYMENT.QR-CODE.LABEL';
    return true;
  }

  private generateOrderLinesSummary(orderLines: OrderLineView[]) {

    // Group the order lines by their product
    let groups: {
      ProductName: string,
      ProductType: ProductTypeEnum,
      EventId: number,
      PassePartoutId: number,
      MembershipId: number,
      MatchDayPassId: number,
      CrossSellProductId: number,
      Count: number,
      PriceTotal: number
    }[] = [];
    for (let orderLine of orderLines) {
      let existingGroup = groups.find(group =>
        group.ProductType === orderLine.ProductType &&
        group.EventId === orderLine.EventId &&
        group.PassePartoutId === orderLine.PassePartoutId &&
        group.MembershipId === orderLine.MembershipId &&
        group.MatchDayPassId === orderLine.MatchDayPassId &&
        group.CrossSellProductId === orderLine.CrossSellProductId
      );
      if (existingGroup) {
        existingGroup.Count++;
        existingGroup.PriceTotal += orderLine.Total;
        continue;
      }

      groups.push({
        ProductName: this.getProductName(orderLine) ?? "Unknown",
        ProductType: orderLine.ProductType,
        EventId: orderLine.EventId,
        PassePartoutId: orderLine.PassePartoutId,
        MembershipId: orderLine.MembershipId,
        MatchDayPassId: orderLine.MatchDayPassId,
        CrossSellProductId: orderLine.CrossSellProductId,
        Count: 1,
        PriceTotal: orderLine.Total
      });
    }

    // Convert to the format specified in TPD-4285
    let result: {LineDescription: string, LineTotal: number}[] = [];
    for (let group of groups) {
      result.push({
        LineDescription: `${group.Count} x ${group.ProductName}`,
        LineTotal: group.PriceTotal
      });
    }

    return result;
  }

  private getProductName(orderLine: OrderLineView) {
    switch (orderLine.ProductType) {
      case 1:
        return orderLine.EventName ?? orderLine.PassePartoutName;
      case 2:
        return "Delivery method";
      case 3:
        return "Payment method";
      case 4:
        return orderLine.MembershipName;
      case 5:
        return "Discount";
      case 6:
        return orderLine.CrossSellProductName;
      case 7:
        return orderLine.MatchDayPassName;
      case 8:
        return "Credit change";
      default:
        return null;
    }
  }

  getBalance() {
    let balance = this.amountOpen;
    if (this.selectedTabId == 'directdebit') {
      balance += this.paymentSurcharge;
    }
    else if (this.selectedTabId == 'cash') {
      balance -= (this.amountPaidPin || 0) + (this.amountPaidCash || 0);
    }
    else if (this.selectedTabId == 'other') {
      balance -= (this.amountPaidOther || 0);
    }
    return balance;
  }

  evalAmount() {
    if (this.getBalance() >= 0) {
      this.form.controls['ConfirmReturnRegister'].setValue(false);
    }
  }

  unCheckConfirmOnChange() {
    this.form.controls.ConfirmCheckComplete.setValue(false);
  }

  private extractEffectiveDates() {
    const effectiveDates = [];
    for (let i = 1; i <= 12; i++) {
      const field = this.formIncasso.controls[`DateEffective${i}`];
      if (!field.value) {
        continue;
      }

      const value = field.value;
      effectiveDates.push(value);
    }

    return effectiveDates;
  }
}

function setIsEnabled(form: FormGroup, enabled: boolean) {
  if (enabled) {
    form.enable();
  }
  else {
    form.disable();
  }
}
